/**
 * Best-effort client for `POST /api/engage/chat/session/delete`.
 *
 * Used by the widget's "Forget me" / clear-conversation action. Failures
 * (network, 5xx) are non-fatal — the caller still performs the local
 * reset (sessionStorage rotation, message clear, autolaunch storage
 * wipe) so the visitor's UX matches their intent even if the server
 * round-trip is temporarily unavailable. Cross-tenant attempts return
 * 404 by design — we treat that as success from the visitor's view.
 */
import type { WidgetConfig } from './widget-config';

export type ForgetSessionOutcome =
  | { readonly ok: true; readonly outcome: 'deleted' | 'noop_missing' }
  | { readonly ok: false; readonly reason: 'network' | 'http_error' };

function sessionDeleteUrl(config: Pick<WidgetConfig, 'apiOrigin'>): string {
  const base = config.apiOrigin.replace(/\/$/, '');
  return `${base}/api/engage/chat/session/delete`;
}

export async function forgetWidgetSession(
  config: Pick<WidgetConfig, 'apiOrigin' | 'tenantId' | 'siteId'>,
  sessionId: string,
): Promise<ForgetSessionOutcome> {
  if (typeof fetch === 'undefined') {
    return { ok: false, reason: 'network' };
  }
  try {
    const res = await fetch(sessionDeleteUrl(config), {
      method: 'POST',
      headers: { 'content-type': 'application/json', accept: 'application/json' },
      credentials: 'omit',
      keepalive: true,
      body: JSON.stringify({
        tenant_id: config.tenantId,
        site_id: config.siteId,
        session_id: sessionId,
      }),
    });
    if (res.status === 404) {
      return { ok: true, outcome: 'noop_missing' };
    }
    if (!res.ok) {
      return { ok: false, reason: 'http_error' };
    }
    const parsed: unknown = await res.json().catch(() => null);
    if (parsed && typeof parsed === 'object') {
      const outcome = (parsed as Record<string, unknown>).outcome;
      if (outcome === 'deleted' || outcome === 'noop_missing') {
        return { ok: true, outcome };
      }
    }
    return { ok: true, outcome: 'deleted' };
  } catch {
    return { ok: false, reason: 'network' };
  }
}
